import React, { useState } from "react";
import axios from "axios";
import { MapPin, Mail, Phone, Send, Youtube } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contatti = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 lg:py-36 bg-[#0A0A0A] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline" data-testid="contact-overline">
              {t.contact.overline}
            </div>
            <h2
              data-testid="contact-title"
              className="font-serif-display font-light mt-5 text-4xl md:text-5xl lg:text-6xl text-[#FDFBF7] leading-[1.05]"
            >
              {t.contact.title}
            </h2>
            <p className="mt-6 text-[#A1A1AA] text-base lg:text-lg font-light leading-relaxed">
              {t.contact.subtitle}
            </p>

            <ul className="mt-12 space-y-7">
              <li className="flex items-start gap-4" data-testid="contact-address">
                <MapPin className="text-[#D4AF37] mt-1 shrink-0" size={18} />
                <div>
                  <div className="overline mb-1">Address</div>
                  <div className="text-[#FDFBF7] text-[15px] font-light">{t.contact.address}</div>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-email">
                <Mail className="text-[#D4AF37] mt-1 shrink-0" size={18} />
                <div>
                  <div className="overline mb-1">Email</div>
                  <a
                    href={`mailto:${t.contact.email}`}
                    className="text-[#FDFBF7] text-[15px] font-light gold-link"
                  >
                    {t.contact.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-phones">
                <Phone className="text-[#D4AF37] mt-1 shrink-0" size={18} />
                <div>
                  <div className="overline mb-1">Phone</div>
                  {t.contact.phones.map((p) => (
                    <a
                      key={p}
                      href={`tel:${p.replace(/[^0-9+]/g, "")}`}
                      className="block text-[#FDFBF7] text-[15px] font-light gold-link mb-1"
                    >
                      {p}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-youtube">
                <Youtube className="text-[#D4AF37] mt-1 shrink-0" size={18} />
                <div>
                  <div className="overline mb-1">YouTube</div>
                  <a
                    href="https://www.youtube.com/@proscenioensemble"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FDFBF7] text-[15px] font-light gold-link"
                  >
                    @proscenioensemble
                  </a>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#0E0E0E] p-8 lg:p-12"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <label className="block">
                  <span className="overline block mb-2">{t.contact.form.name} *</span>
                  <input
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange}
                    placeholder={t.contact.form.name}
                    className="input-dark"
                    data-testid="contact-input-name"
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-2">{t.contact.form.email} *</span>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    placeholder="name@email.com"
                    className="input-dark"
                    data-testid="contact-input-email"
                  />
                </label>
              </div>

              <label className="block mt-6">
                <span className="overline block mb-2">{t.contact.form.subject}</span>
                <input
                  name="subject"
                  type="text"
                  value={form.subject}
                  onChange={onChange}
                  placeholder={t.contact.form.subject}
                  className="input-dark"
                  data-testid="contact-input-subject"
                />
              </label>

              <label className="block mt-6">
                <span className="overline block mb-2">{t.contact.form.message} *</span>
                <textarea
                  name="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  placeholder={t.contact.form.message}
                  className="input-dark resize-none"
                  data-testid="contact-input-message"
                />
              </label>

              <div className="mt-8 flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E8C85F] disabled:opacity-60 disabled:cursor-not-allowed text-black px-8 py-4 text-xs tracking-[0.25em] uppercase font-medium transition-all duration-300"
                >
                  {loading ? t.contact.form.sending : t.contact.form.send}
                  <Send size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contatti;
